import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { v4 as uuidv4 } from 'uuid';
import {
    HistoryModel,
    MessageModel,
    BulkExportResult,
    ExportConfig,
} from '../types/models';
import { apiClient } from '../clients/APIClient';
import { parseTimestampMs } from '../utils/dateTime';
import { usePlatformStore } from './platformStore';
import { useChatHistoryStore } from './chatHistoryStore';
import { useChatUIStore } from './chatUIStore';
import { useDocumentStore } from './documentStore';
import { loadRegisteredStores } from './settingsRegistry';

export type ChatListStore = {
    chats: HistoryModel[];
    refreshChats: () => Promise<void>;
    selectChat: (chatId: string) => Promise<void>;
    renameChat: (chatId: string, title?: string) => Promise<void>;
    removeChat: (chatId: string) => Promise<void>;
    onNewChat: () => void;
    onModelChange: () => void;
    startInitialConnection: () => Promise<void>;
    cleanupConnectionRetry: () => void;
    stopGeneration: () => Promise<void>;
    exportChat: (
        chatId: string,
        theme: 'light' | 'dark',
        format: 'pdf' | 'markdown' | 'txt',
        model: string,
        config?: ExportConfig,
    ) => Promise<boolean>;
    exportBulk: (
        chatIds: string[],
        theme: 'light' | 'dark',
        format: 'pdf' | 'markdown' | 'txt',
        model: string,
        config?: ExportConfig,
        onProgress?: (current: number, total: number, chatId: string) => void,
    ) => Promise<BulkExportResult>;
    handleExportOpenFile: () => void;
    handleExportShowInFolder: () => void;
};

const enableDevtools = process.env.NODE_ENV !== 'production';
export const useChatListStore = create<ChatListStore>()(
    devtools(
        (set, get) => ({
            chats: [],

            refreshChats: async () => {
                const chatUI = useChatUIStore.getState();

                try {
                    const res = await apiClient.history.listHistory();
                    if (Array.isArray(res)) {
                        const sorted = [...res].sort(
                            (a, b) => parseTimestampMs(b.last_updated) - parseTimestampMs(a.last_updated),
                        );

                        set(
                            (state) => {
                                if (chatUI.initialLoad && state.chats.length > 0) {
                                    const prevMap = new Map(
                                        state.chats.map((c) => [c.id, c]),
                                    );

                                    sorted.forEach((newChat) => {
                                        const oldChat = prevMap.get(newChat.id);
                                        if ( oldChat && (oldChat.title !== newChat.title || oldChat.last_updated !== newChat.last_updated)) {
                                            // Chat was updated - dispatch incremental update
                                            window.dispatchEvent(
                                                new CustomEvent('search-index-update', {
                                                    detail: {
                                                        operation: 'update',
                                                        sourceType: 'chat',
                                                        id: newChat.id,
                                                        title: newChat.title,
                                                        lastModified: newChat.last_updated,
                                                    },
                                                }),
                                            );
                                        } else if (!oldChat) {
                                            // New chat - dispatch add
                                            window.dispatchEvent(
                                                new CustomEvent('search-index-update', {
                                                    detail: {
                                                        operation: 'add',
                                                        sourceType: 'chat',
                                                        id: newChat.id,
                                                        title: newChat.title,
                                                        lastModified: newChat.last_updated,
                                                    },
                                                }),
                                            );
                                        }
                                    });
                                }

                                return { chats: sorted };
                            },
                            false,
                            'chatList/refreshChats(success)',
                        );
                        chatUI.setInitialLoad(true);
                    } else {
                        console.error('Fetched chat history is not an array:', res);
                        set(
                            { chats: [] },
                            false,
                            'chatList/refreshChats(invalid)',
                        );
                    }
                } catch (err) {
                    console.error('Error fetching chat history:', err);
                    set({ chats: [] }, false, 'chatList/refreshChats(error)');
                }
            },

            selectChat: async (chatId) => {
                const chatHistory = useChatHistoryStore.getState();

                if (chatHistory.isGenerating) {
                    await get().stopGeneration();
                }

                try {
                    const res = await apiClient.history.getHistoryById(chatId);
                    // Check if the response is an error object
                    // Handles when newly created chatID is not yet created on the backend
                    // Also handles other errors resulting in an empty chat without crashing the UI
                    if (res && typeof res === 'object' && 'error' in res) {
                        console.log(`Chat history ${chatId} not found on backend yet, initializing empty chat`);
                        chatHistory.setHistory([]);
                        chatHistory.setInputs([]);
                        chatHistory.setChatId(chatId);
                        chatHistory.setInUseVLM(false);
                        return;
                    }

                    const chatMessages = Array.isArray(res) ? (res as MessageModel[]) : [];
                    chatHistory.setHistory(chatMessages);
                    chatHistory.setInputs(chatMessages);

                    const modelsWithValues = chatMessages
                        .map((chat) => chat.model)
                        .filter((model) => model);

                    chatHistory.setSelectedModel(
                        modelsWithValues[modelsWithValues.length - 1] || '',
                    );
                    chatHistory.setChatId(chatId);

                    const containsImage = chatHistory.checkIfChatContainsImage(chatMessages);
                    chatHistory.setInUseVLM(containsImage);
                } catch (err) {
                    console.error('Unexpected error selecting chat:', err);
                }
            },

            renameChat: async (chatId, title) => {
                if (!title || !title.trim()) return;

                const newTitle = title.trim();
                let previousTitle: string | undefined;

                set(
                    (state) => ({
                        chats: state.chats.map((c) => {
                            if (c.id === chatId) {
                                previousTitle = c.title;
                                return { ...c, title: newTitle };
                            }
                            return c;
                        }),
                    }),
                    false,
                    'chatList/renameChat',
                );

                try {
                    // Fetch the correct messages for the target chat to satisfy backend requirements
                    const targetMessages = await apiClient.history
                        .getHistoryById(chatId)
                        .catch(() => [] as MessageModel[]);

                    await apiClient.history.updateHistoryById(
                        chatId,
                        targetMessages,
                        newTitle,
                    );
                } catch (error) {
                    console.error('Rename failed, rolling back title', error);
                    if (previousTitle !== undefined) {
                        set(
                            (state) => ({
                                chats: state.chats.map((c) =>
                                    c.id === chatId
                                        ? { ...c, title: previousTitle as string }
                                        : c,
                                ),
                            }),
                            false,
                            'chatList/renameChat(rollback)',
                        );
                    }
                } finally {
                    await get().refreshChats();
                }
            },

            removeChat: async (chatId) => {
                const chatHistoryState = useChatHistoryStore.getState();
                const isDeletingActiveChat = chatHistoryState.chatId === chatId;

                // If deleting the active chat, stop generation and reset to new chat immediately
                if (isDeletingActiveChat) {
                    if (chatHistoryState.isGenerating) {
                        await get().stopGeneration();
                    }
                    get().onNewChat();
                }

                set(
                    (state) => ({
                        chats: state.chats.filter((c) => c.id !== chatId),
                    }),
                    false,
                    'chatList/removeChat',
                );
                
                try {
                    console.log(`[chatListStore] Removing chat ${chatId}`);
                    await apiClient.history.deleteHistoryById(chatId);

                    window.dispatchEvent(
                        new CustomEvent('search-index-update', {
                            detail: {
                                operation: 'remove',
                                sourceType: 'chat',
                                id: chatId,
                            },
                        }),
                    );

                    await get().refreshChats();
                } catch (err) {
                    console.error(err);
                    // Refresh to restore accurate state on failure
                    await get().refreshChats();
                }
            },

            onNewChat: () => {
                
                const chatHistory = useChatHistoryStore.getState();
                

                chatHistory.resetCurrentChatState(uuidv4());
                window.dispatchEvent(new CustomEvent('stop-tts'));
            },

            onModelChange: () => {
                useChatHistoryStore.getState().setIsGenerating(false);
            },

            startInitialConnection: async () => {
                let handled = false;

                const onReady = async () => {
                    if (handled) return;
                    handled = true;
                    // Backend confirmed ready — load all data
                    useDocumentStore.getState().attemptDataLoad();
                    loadRegisteredStores();
                    await get().refreshChats();
                    useChatUIStore.getState().setIsSplashVisible(false);
                };

                // If already ready (e.g. hot reload), load immediately
                if (usePlatformStore.getState().isBackendReady) {
                    await onReady();
                    return;
                }

                // Subscribe and react when backend becomes ready
                const unsubscribe = usePlatformStore.subscribe((state) => {
                    if (state.isBackendReady) {
                        unsubscribe();
                        void onReady();
                    }
                });
            },

            cleanupConnectionRetry: () => {
                // No-op — retry logic is now in platformStore.waitForBackend
            },

            stopGeneration: async () => {
                await useChatHistoryStore.getState().stopGeneration();
                try {
                    await get().refreshChats();
                } catch (error) {
                    console.error('Error refreshing chats after stop:', error);
                }
            },

            handleExportOpenFile: () => {
                const { lastExportFilePath } = useChatUIStore.getState();
                if (lastExportFilePath && window.electron) {
                    window.electron.openExportedFile(lastExportFilePath);
                }
            },

            handleExportShowInFolder: () => {
                const { lastExportFilePath, lastExportDirectory } =
                    useChatUIStore.getState();

                if (!window.electron) return;
                if (lastExportFilePath) {
                    window.electron.showExportedFileInFolder(lastExportFilePath);
                } else if (lastExportDirectory) {
                    window.electron.showExportedDirectory(lastExportDirectory);
                }
            },
        }),
        { enabled: enableDevtools, name: 'chat-list-store' },
    ),
);
